import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Heart, DollarSign, Users, Check, ChevronDown, ExternalLink, Copy, Star } from 'lucide-react';
import { Meta } from '../components/Meta';
import { supabase } from '../lib/supabase';

const faqs = [
  {
    question: 'How do I get my referral code?',
    answer: 'Once you become an ambassador, your unique referral code will be available in your profile settings. You can share it anywhere — social media, in-person, or with clients.'
  },
  {
    question: 'When do commissions get paid?',
    answer: 'Commissions are paid monthly via Stripe once your balance reaches £10. You\'ll receive your earnings on the last business day of each month.'
  },
  {
    question: 'Does the referral period expire?',
    answer: 'Each referral earns you commission for up to 12 months from their first paid subscription. After 12 months, you can still benefit from their continued membership — but the commission period resets if they upgrade their plan.'
  },
  {
    question: 'Can I share on social media?',
    answer: 'Absolutely! We encourage sharing on Instagram, Facebook, Twitter, and LinkedIn. You can generate custom links from your ambassador dashboard.'
  },
  {
    question: 'What counts as a paid conversion?',
    answer: 'You earn commission when someone signs up for a paid subscription using your referral code. Free accounts and trial users don\'t count toward your earnings.'
  }
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-center justify-between text-left"
      >
        <span className="font-medium text-content text-lg">{question}</span>
        <ChevronDown
          className={`h-5 w-5 text-content/60 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="pb-5 text-content/70 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

export function Ambassador() {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setIsAuthenticated(!!session);
      if (session?.user?.email) {
        setUserEmail(session.user.email);
        // Fetch username
        const { data } = await supabase
          .from('users')
          .select('username')
          .eq('id', session.user.id)
          .maybeSingle();
        if (data?.username) setUsername(data.username);
      }
      setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setIsAuthenticated(!!session);
      if (session?.user?.email) {
        setUserEmail(session.user.email);
        // Fetch username
        const { data } = await supabase
          .from('users')
          .select('username')
          .eq('id', session.user.id)
          .maybeSingle();
        if (data?.username) setUsername(data.username);
      } else {
        setUsername(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const referralCount = 0; // Would come from backend
  const monthlyEarnings = 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F3F7EE] to-white">
      <Meta
        title="Become an Ambassador | Mindful Family"
        description="Join the Mindful Family ambassador program. Share wellness, earn rewards, and help more families find their calm."
      />

      {/* Hero Section */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-text/10 rounded-full text-accent-text font-medium text-sm mb-6">
            <Heart className="h-4 w-4" />
            Join Our Ambassador Community
          </div>
          <h1 className="text-4xl lg:text-5xl font-gelica text-content leading-tight mb-6">
            Spread Wellness.{' '}
            <span className="text-accent-text">Earn Together.</span>
          </h1>
          <p className="text-xl text-content/70 max-w-2xl mx-auto mb-10">
            Become a Mindful Family ambassador and share a platform you believe in.
            Earn commissions while helping more people discover holistic wellness.
          </p>
          {!isLoading && (
            isAuthenticated && username ? (
              <Link
                to={`/profile/${username}/referrals`}
                className="inline-flex items-center gap-2 px-8 py-4 bg-accent-text text-white text-lg font-semibold rounded-xl hover:bg-accent-text/90 active:scale-[0.98] transition-all"
              >
                Go to Your Dashboard
                <ExternalLink className="h-5 w-5" />
              </Link>
            ) : (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('show-auth'))}
                className="inline-flex items-center gap-2 px-8 py-4 bg-accent-text text-white text-lg font-semibold rounded-xl hover:bg-accent-text/90 active:scale-[0.98] transition-all"
              >
                Become an Ambassador
                <ExternalLink className="h-5 w-5" />
              </button>
            )
          )}
        </div>
      </div>

      {/* Why Be an Ambassador */}
      <div className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-gelica text-content text-center mb-12">
            Why Become an Ambassador?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg shadow-black/5 text-center">
              <div className="w-16 h-16 bg-[#E8F5E9] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Heart className="h-8 w-8 text-[#4CAF50]" />
              </div>
              <h3 className="text-xl font-semibold text-content mb-3">You Believe in It</h3>
              <p className="text-content/70">
                Share something you actually use and love. Your clients and community already trust your recommendations.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg shadow-black/5 text-center">
              <div className="w-16 h-16 bg-[#FFF3E0] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <DollarSign className="h-8 w-8 text-[#FF9800]" />
              </div>
              <h3 className="text-xl font-semibold text-content mb-3">You Earn From It</h3>
              <p className="text-content/70">
                Earn 20% commission on all referrals for up to 12 months. Payouts via Stripe from just £10.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg shadow-black/5 text-center">
              <div className="w-16 h-16 bg-[#E3F2FD] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-[#2196F3]" />
              </div>
              <h3 className="text-xl font-semibold text-content mb-3">You Grow With It</h3>
              <p className="text-content/70">
                Join an exclusive ambassador community, get early access to new features, and unlock special perks.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-gelica text-content text-center mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '1', title: 'Sign Up', desc: 'Create your ambassador account — free and instant' },
              { step: '2', title: 'Share', desc: 'Get your unique referral code and share it with your network' },
              { step: '3', title: 'Invite', desc: 'Friends and clients sign up using your code' },
              { step: '4', title: 'Earn', desc: 'Earn 20% commission for 12 months on their subscription' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 bg-accent-text/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-accent-text">{item.step}</span>
                </div>
                <h3 className="text-lg font-semibold text-content mb-2">{item.title}</h3>
                <p className="text-content/60 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-content/60 mt-8">
            Payouts via Stripe, from just £10
          </p>
        </div>
      </div>

      {/* What You Earn */}
      <div className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-gelica text-content text-center mb-4">
            What You Earn
          </h2>
          <p className="text-center text-content/60 mb-12">
            Earn 20% commission on each referral's subscription for up to 12 months
          </p>
          <div className="bg-white rounded-2xl shadow-lg shadow-black/5 overflow-hidden">
            <div className="grid grid-cols-3 bg-accent-text/5 font-semibold text-content text-center py-4 px-4">
              <span>Referrals</span>
              <span>Monthly Earnings</span>
              <span>Annual Earnings</span>
            </div>
            {[
              { referrals: 1, monthly: '£1', annual: '£12' },
              { referrals: 5, monthly: '£5', annual: '£60' },
              { referrals: 10, monthly: '£10', annual: '£120' },
              { referrals: 20, monthly: '£20', annual: '£240' },
              { referrals: 50, monthly: '£50', annual: '£600' },
              { referrals: 100, monthly: '£100', annual: '£1,200' }
            ].map((row, index) => (
              <div key={index} className="grid grid-cols-3 text-center py-4 px-4 border-t border-gray-100">
                <span className="font-medium text-content">{row.referrals}</span>
                <span className="text-accent-text font-semibold">{row.monthly}</span>
                <span className="text-content/70">{row.annual}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-content/50 text-sm mt-6">
            * Based on average £5/month subscription. Actual earnings vary by plan type.
          </p>
        </div>
      </div>

      {/* Who It's For */}
      <div className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-gelica text-content text-center mb-12">
            Who It's For
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-[#F3F7EE] to-white rounded-2xl p-8 border border-gray-100">
              <div className="w-14 h-14 bg-accent-text/10 rounded-xl flex items-center justify-center mb-6">
                <Users className="h-7 w-7 text-accent-text" />
              </div>
              <h3 className="text-xl font-semibold text-content mb-3">Wellness Practitioners</h3>
              <p className="text-content/70">
                Your clients already trust you. Let your referral code be part of your welcome pack.
                Share Mindful Family as a resource for their wellness journey.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#F3F7EE] to-white rounded-2xl p-8 border border-gray-100">
              <div className="w-14 h-14 bg-accent-text/10 rounded-xl flex items-center justify-center mb-6">
                <Star className="h-7 w-7 text-accent-text" />
              </div>
              <h3 className="text-xl font-semibold text-content mb-3">Community Members</h3>
              <p className="text-content/70">
                Love the platform? Your friends and family will too — and you'll be rewarded for it.
                Turn your recommendation into income.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-gelica text-content text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="bg-white">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </div>

      {/* CTA Footer */}
      <div className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-gelica text-content mb-4">
            Ready to earn while doing good?
          </h2>
          <p className="text-content/60 mb-8">
            Join hundreds of wellness advocates already sharing Mindful Family
          </p>
          {!isLoading && (
            isAuthenticated && username ? (
              <Link
                to={`/profile/${username}/referrals`}
                className="inline-flex items-center gap-2 px-10 py-4 bg-accent-text text-white text-lg font-semibold rounded-xl hover:bg-accent-text/90 active:scale-[0.98] transition-all"
              >
                Go to Your Dashboard
                <ExternalLink className="h-5 w-5" />
              </Link>
            ) : (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('show-auth'))}
                className="inline-flex items-center gap-2 px-10 py-4 bg-accent-text text-white text-lg font-semibold rounded-xl hover:bg-accent-text/90 active:scale-[0.98] transition-all"
              >
                Become an Ambassador
                <ExternalLink className="h-5 w-5" />
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default Ambassador;
